import { GetStaticProps, NextPage } from "next";
import { PageProps } from "../../types/pageProps";
import { Previewable } from "../../types/previewable";
import { getStaticPageData } from "../../utils/getStaticPageData";
import { revalidationInterval } from "../../utils/revalidationInterval";
import { isEmpty } from "lodash";

import "pure-react-carousel/dist/react-carousel.es.css";
import {
  PricingLandingProfessionalsData,
  getPricingLandingProfessionalsQuery,
} from "../../queries/pricingLandingProfessionalsQuery";
import Block from "../../components/block";
import { QueryParams } from "next-sanity";
import LiveQuery from "@sanity/preview-kit/live-query";
import PricingPlans from "@/components/PricingPlans/PricingPlans";
import SlidingTestimonialsCarousel from "@/components/SlidingTestimonialsCarousel/SlidingTestimonialsCarousel";
import HeaderNavigation from "@/components/HeaderNavigation/HeaderNavigation";
import {
  HeaderNavigationProps,
  HeaderNavigationQuery,
} from "@/components/HeaderNavigation";
import { AdoptionPlansHero } from "../../components/AdoptionPlansHero";
import { getClient } from "lib/sanity.server";

import HeroWithForm from "@/components/HeroWithForm/HeroWithForm";
import Questions from "../../components/questions";
import MultiColumnSection from "@/components/MultiColumnSection/MultiColumnSection";
import SEOPageHead from "@/components/SEOPageHead";
import { FooterModular } from "@/components/FooterModular";
import {
  FooterNavigationProps,
  FooterNavigationQuery,
} from "@/components/FooterNavigation/query";

export type PricingLandingProps = Previewable<
  PageProps<PricingLandingProfessionalsData>
>;

const token = process.env.SANITY_API_TOKEN;

const PricingLandingProfessionals: NextPage<PricingLandingProps> = (
  props: PricingLandingProps,
) => {
  const pricingLandingProfessionalsData = props.payload.data;
  if (pricingLandingProfessionalsData === null) {
    return null;
  }

  const {
    form,
    hero,
    colorway,
    pricing,
    testimonials,
    moduleQuestions,
    navSections,
    services,
    seo,
  }: PricingLandingProfessionalsData = pricingLandingProfessionalsData;

  return (
    <>
      <LiveQuery
        enabled={props.preview}
        query={getPricingLandingProfessionalsQuery(props.preview)}
        initialData={props.payload.data}
      >
        {seo && <SEOPageHead seo={seo} />}
        <HeaderNavigation navSections={navSections} />

        {!isEmpty(hero) && !isEmpty(colorway) && (
          <AdoptionPlansHero colorway={colorway} hero={hero} />
        )}

        {!isEmpty(pricing) && (
          <Block>
            <PricingPlans {...pricing} />
          </Block>
        )}

        {!isEmpty(services) &&
          services.map((service, i) => (
            <MultiColumnSection key={i} colorway={colorway} {...service} />
          ))}

        {!isEmpty(testimonials) && (
          <SlidingTestimonialsCarousel {...testimonials} colorway={colorway} />
        )}

        {!isEmpty(form) && <HeroWithForm {...form} colorway={colorway} />}

        {moduleQuestions && moduleQuestions.moduleFeatureFlag?.enabled && (
          <Block color="darkblue" flushBottom>
            <Questions moduleInfo={{ moduleQuestions }} />
          </Block>
        )}
        <FooterModular footerData={props.payload.data.footerPayload} />
      </LiveQuery>
    </>
  );
};

export const getStaticProps: GetStaticProps<PricingLandingProps> = async (
  context,
) => {
  const preview: boolean = !!context.draftMode;
  const queryParams: QueryParams = {};
  const query = getPricingLandingProfessionalsQuery(preview);

  const pricingPayload =
    await getStaticPageData<PricingLandingProfessionalsData>({
      query,
      queryParams,
      preview,
    });

  if (pricingPayload === null) {
    return {
      notFound: true,
    };
  }

  const navPayload: HeaderNavigationProps = await getClient(preview).fetch(
    HeaderNavigationQuery.query,
    {},
  );

  const footerPayload: FooterNavigationProps = await getClient(preview).fetch(
    FooterNavigationQuery.query,
    {},
  );

  if (isEmpty(navPayload)) {
    return {
      notFound: true,
    };
  }

  const payloadWithNav = {
    ...pricingPayload,
    data: {
      ...pricingPayload.data,
      ...navPayload,
      footerPayload: footerPayload,
    },
  };

  return {
    props: {
      payload: { ...payloadWithNav },
      preview,
      token: preview ? token : "",
    },
    revalidate: revalidationInterval,
  };
};

export default PricingLandingProfessionals;
